import "components/filter/Filter.css";

import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  Header,
  List,
  Modal,
} from "semantic-ui-react";

import Collapsible from "react-collapsible";
import MediaQuery from "react-responsive";
import MediaQueryHelper from "static/MediaQueryHelper";
import React from "react";
import Tags from "static/Tags";
import _ from "underscore";

const styles = {
  filterList: {
    marginTop: 4,
    marginBottom: 4,
    marginRight: 14,
    marginLeft: 0,
  },
  filterHeader: {
    marginBottom: 0,
  },
  filterSection: {
    marginTop: 12,
    marginBottom: 12,
  },
  filterButton: {
    borderRadius: 6,
  },
};

/**
 * Transform raw tag definitions into a structure
 * that is easier to render in the UI.
 *
 * Result shape:
 * {
 *   cost: [ { tag, displayName, show, category }, ... ],
 *   accessibility: [ ... ]
 * }
 */
function Filter(props) {
  const filterList = transformTagsToFilterList(Tags.getAllTags());

  function transformTagsToFilterList(tags) {
    _.each(tags, (val, key) => {
      val["tag"] = key;
    });

    return _.groupBy(_.values(tags), "category");
    // group by tags' category value, check underscore.js doc for build-in methods
  }

  /**
   * Desktop filter layout:
   * - One card per category
   * - Visible tags shown by default
   * - Hidden tags revealed via "Show More"
   */
  function createGroups() {
    return _.map(filterList, (val, category) => {
      return (
        <Grid.Row key={category} className="filter-row">
          <Card>
            <Card.Content>
              <Card.Header>{getDisplayNameForCategory(category)}</Card.Header>
            </Card.Content>
            <Card.Content>{createList(category, val)}</Card.Content>
            <CardContent style={{ border: "none", paddingTop: "0px" }}>
              <Collapsible
                trigger="SHOW MORE ▼"
                triggerWhenOpen="SHOW LESS ▲"
                transitionTime={310}
                triggerStyle={{
                  cursor: "pointer",
                  color: "orange",
                  fontWeight: "500",
                }}
              >
                {createSubList(category, val)}
              </Collapsible>
            </CardContent>
          </Card>
        </Grid.Row>
      );
    });
  }

  // Capitalize category names for desktop headers
  function getDisplayNameForCategory(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /**
   * Render hidden tags inside the "Show More" section.
   * These are tags where item.show === false.
   */
  function createSubList(category, items) {
    return items.map((item) => {
      // Find if the item is currently selected
      let existingItem = _.find(props.filter[category], (currItem) => {
        return currItem === item.tag;
      });

      return (
        <List.Item key={item.tag} style={{ marginTop: "5px" }}>
          {item.show ? (
            ""
          ) : (
            <div className="tag-label">
            <Checkbox className={`tag-label tag-${item.tag}`}
              checked={existingItem ? true : false}
              onClick={() => props.onFilterChange(category, item.tag)}
              label={
                item.displayName
              }
            />
            </div>
          )}
        </List.Item>
      );
    });
  }

  // Find if the item is currently selecteds
  // see App.js (state.filter )
  function createList(category, items) {
    return items.map((item) => {
      let existingItem = _.find(props.filter[category], (currItem) => {
        return currItem === item.tag;
      });
      // find return the first matching

      return (
        <List.Item key={item.tag} style={{ marginTop: "5px" }}>
          {item.show ? (
            <div className="tag-label">
            <Checkbox 
              className={`tag-label tag-${item.tag}`}
              checked={existingItem ? true : false}
              onClick={() => props.onFilterChange(category, item.tag)}
              
              label={
                item.displayName
              }
            />
            </div>
          ) : (
            ""
          )}
        </List.Item>
      );
    });
  }

  /**
   * Mobile filter UI:
   * - Rendered inside a modal
   * - Uses toggle buttons instead of checkboxes
   */
  function renderMobileFilter() {
    return (
      <Modal open={props.open} onClose={props.onCloseFilter}>
        <Modal.Header>
          <div className="filter-header">
            <Header as="h2" style={styles.filterHeader}>
              Filters
            </Header>
            <div>
              <button className="control-button" onClick={props.onClearFilter}>
                CLEAR ALL
              </button>
              <button
                className="control-button green"
                onClick={props.onCloseFilter}
              >
                APPLY
              </button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content>{createMobileGroups()}</Modal.Content>
      </Modal>
    );
  }

  /**
   * Mobile groups are flattened and rendered horizontally
   * for better touch interaction.
   */
  function createMobileGroups() {
    return _.map(filterList, (val, category) => {
      return (
        <div key={category}>
          <h5 className="mobile-group-title">
            {getMobileDisplayNameForCategory(category)}
          </h5>
          <List horizontal style={styles.filterSection}>
            {createMobileList(category, val)}
          </List>
        </div>
      );
    });
  }

  function getMobileDisplayNameForCategory(category) {
    const displayName =
      category === "additional" ? "Additional Tags" : category;
    return displayName.toUpperCase();
  }

  /**
   * Mobile filter buttons:
   * - Toggle-style buttons instead of checkboxes
   */
  function createMobileList(category, items) {
    return items.map((item) => {
      // Find if the item is currently selected
      let existingItem = _.find(props.filter[category], (currItem) => {
        return currItem === item.tag;
      });

      return (
        <List.Item key={item.tag} style={styles.filterList}>
          <Button
            compact
            toggle
            size="small"
            style={styles.filterButton}
            active={existingItem ? true : false}
            onClick={() => props.onFilterChange(category, item.tag)}
            content={item.displayName}
          />
        </List.Item>
      );
    });
  }

  return (
    <div>
      <MediaQuery minDeviceWidth={MediaQueryHelper.MIN_WIDTH_TABLET}>
        {/* Laptop */}
        {createGroups()}
        <Grid.Row>
          <Button
            onClick={props.onClearFilter}
            content="Clear Filter"
            primary
          />
        </Grid.Row>
      </MediaQuery>
      <MediaQuery maxDeviceWidth={MediaQueryHelper.MIN_WIDTH_TABLET}>
        {/* Mobile and tablet */}
        {renderMobileFilter()}
      </MediaQuery>
    </div>
  );
}

export default Filter;
